// House envelope [02 §6.2]: success `{data, error: null, meta?}`, failure `{data: null, error}`.
import { z } from 'zod';
import { AppError, ERROR_CODE_LIST, toAppError } from './errors.ts';
import type { ErrorCode, ErrorParams, FieldError } from './errors.ts';

const paramValue = z.union([z.string(), z.number(), z.boolean(), z.null()]);

export const fieldErrorSchema = z.object({
  path: z.string(),
  code: z.string(),
  messageKey: z.string(),
  params: z.record(z.string(), paramValue).optional(),
});

export const errorBodySchema = z.object({
  code: z.enum(ERROR_CODE_LIST as [ErrorCode, ...ErrorCode[]]),
  messageKey: z.string().min(1),
  /** Localised from `messageKey` by the API (EN/FR); says what to do [BR-API-001]. */
  message: z.string(),
  params: z.record(z.string(), paramValue).optional(),
  gate: z.string().optional(),
  overridable: z.boolean().optional(),
  fields: z.array(fieldErrorSchema).optional(),
  details: z.record(z.string(), z.unknown()).optional(),
  requestId: z.string().optional(),
});
export type ErrorBody = z.infer<typeof errorBodySchema>;

export interface PageMeta {
  readonly nextCursor: string | null;
  readonly prevCursor: string | null;
  readonly limit: number;
  readonly total?: number;
  readonly totalCapped?: boolean;
}

export const pageMetaSchema = z.object({
  nextCursor: z.string().nullable(),
  prevCursor: z.string().nullable(),
  limit: z.number().int().positive(),
  total: z.number().int().nonnegative().optional(),
  totalCapped: z.boolean().optional(),
});

export interface Ok<T, M = undefined> {
  readonly data: T;
  readonly error: null;
  readonly meta?: M;
}
export interface Fail {
  readonly data: null;
  readonly error: ErrorBody;
}
export type Envelope<T, M = undefined> = Ok<T, M> | Fail;

export function ok<T>(data: T): Ok<T>;
export function ok<T, M>(data: T, meta: M): Ok<T, M>;
export function ok<T, M>(data: T, meta?: M): Ok<T, M> {
  return meta === undefined ? { data, error: null } : { data, error: null, meta };
}

export function fail(error: ErrorBody): Fail {
  return { data: null, error };
}

/** Translator injected by the API/i18n layer: messageKey + params → localised text. */
export type Translate = (messageKey: string, params: ErrorParams) => string;

function fieldOut(f: FieldError): z.infer<typeof fieldErrorSchema> {
  return f.params
    ? { path: f.path, code: f.code, messageKey: f.messageKey, params: { ...f.params } }
    : { path: f.path, code: f.code, messageKey: f.messageKey };
}

/**
 * Maps any thrown value to an error body. INTERNAL never exposes the cause, stack, SQL or table
 * names [02 §6.2]; the caller logs those under the same requestId.
 */
export function toErrorBody(
  value: unknown,
  opts: { readonly translate: Translate; readonly requestId?: string },
): ErrorBody {
  const err: AppError = toAppError(value);
  const body: ErrorBody = {
    code: err.code,
    messageKey: err.messageKey,
    message: opts.translate(err.messageKey, err.params),
  };
  if (Object.keys(err.params).length) body.params = { ...err.params };
  if (err.gate !== undefined) body.gate = err.gate;
  if (err.code === 'GATE_BLOCKED') body.overridable = err.overridable;
  if (err.fields.length) body.fields = err.fields.map(fieldOut);
  if (err.details && err.code !== 'INTERNAL') body.details = { ...err.details };
  if (opts.requestId !== undefined) body.requestId = opts.requestId;
  return body;
}

export function failFrom(
  value: unknown,
  opts: { readonly translate: Translate; readonly requestId?: string },
): Fail {
  return fail(toErrorBody(value, opts));
}

/** Zod schema for an envelope around `data` (used by API contract tests and the web client). */
export function envelopeSchema<T extends z.ZodType, M extends z.ZodType | undefined = undefined>(
  data: T,
  meta?: M,
) {
  const okShape = z.object({
    data,
    error: z.null(),
    ...(meta ? { meta } : {}),
  });
  const failShape = z.object({ data: z.null(), error: errorBodySchema });
  return z.union([okShape, failShape]);
}

/** Converts a zod error into VALIDATION_FAILED with `fields[]` (paths joined with dots). */
export function fromZodError(error: z.ZodError, messageKeyPrefix = 'errors.validation'): AppError {
  const fields: FieldError[] = error.issues.map((issue) => ({
    path: issue.path.map(String).join('.'),
    code: issue.code,
    messageKey: `${messageKeyPrefix}.${issue.code}`,
  }));
  return new AppError('VALIDATION_FAILED', { fields });
}
